
import RcDomainObject from "../../common/RcDomainObject";
import Company from "../company/Company.js";
import CheckListItemMap from "./item/CheckListItemMap.js";
import CheckListMap from "./CheckListMap.js";
import StringUtils from '@/utils/StringUtils.js'
import User from "../user/User.js";

export default class CheckList extends RcDomainObject {
  
  static MODEL_NAME = 'CheckList';
  
  static Fields = {
      Name: "name",
      Description: "description",
      CREATED_DATE: "createdDate",
      COMPANY: "Company",
      CheckListItemMap: CheckListItemMap.MODEL_NAME,
      Kind: "kind",
      Declaration: "declaration",
      ScheduleNumber: "scheduleNumber",
      PrintLayout: "printLayout",
  }
  
  static Values = {
      // Missing means an ordinary company checklist.
      Kind: {
        BuiltIn: "builtIn",
      },
      // Missing means the standard layout.
      PrintLayout: {
        Schedule1: "schedule1",
      },
  }
  
  constructor(domain, data) {
    super(domain, data, CheckList.MODEL_NAME);
  }

  fields() {
    return CheckList.Fields;
  }
  
  find() {
    var result = this.domain.checklists().findById(this.id());
    if (result.id() !== this.id()) {
      result = this;
    }
    
    return result;
  }

  name() {
    return this.getString(CheckList.Fields.Name);
  }
  
  withName(name) {
    this.put(CheckList.Fields.Name, name);
    return this;
  }
  
  description() {
    return this.getString(CheckList.Fields.Description);
  }
  
  withDescription(description) {
    this.put(CheckList.Fields.Description, description);
    return this;
  }
  
  hasDescription() {
    return !StringUtils.isEmpty(this.description());
  }
  
  kind() {
    return this.getString(CheckList.Fields.Kind);
  }
  
  withKind(kind) {
    this.put(CheckList.Fields.Kind, kind);
    return this;
  }
  
  // A checklist ReadyChek ships, such as an MTO schedule. It belongs to no company,
  // and the server refuses any save that would change it - the kind alone decides.
  isBuiltIn() {
    return StringUtils.equals(CheckList.Values.Kind.BuiltIn, this.kind());
  }
  
  // The statement the driver agrees to when signing off, as printed on the schedule.
  declaration() {
    return this.getString(CheckList.Fields.Declaration);
  }
  
  withDeclaration(declaration) {
    this.put(CheckList.Fields.Declaration, declaration);
    return this;
  }
  
  hasDeclaration() {
    return !StringUtils.isEmpty(this.declaration());
  }
  
  // The regulation's own number for the schedule, such as "1" for MTO Schedule 1.
  // A string, not a number: it is a label, and some are not numeric.
  scheduleNumber() {
    return this.getString(CheckList.Fields.ScheduleNumber);
  }
  
  withScheduleNumber(scheduleNumber) {
    this.put(CheckList.Fields.ScheduleNumber, scheduleNumber);
    return this;
  }
  
  hasScheduleNumber() {
    return !StringUtils.isEmpty(this.scheduleNumber());
  }
  
  // How a completed checklist is printed. Empty means the standard layout.
  printLayout() {
    return this.getString(CheckList.Fields.PrintLayout);
  }
  
  withPrintLayout(printLayout) {
    this.put(CheckList.Fields.PrintLayout, printLayout);
    return this;
  }
  
  isPrintLayoutSchedule1() {
    return StringUtils.equals(CheckList.Values.PrintLayout.Schedule1, this.printLayout());
  }
  
  items() {
    var data = this.get(CheckList.Fields.CheckListItemMap);
    var map = new CheckListItemMap(this.domain, data);
    return map;
  }
  
  // Information blocks are items but not questions, so anything reporting a
  // question count has to leave them out.
  questionCount() {
    var resolved = this.items().find();
    if (resolved.size() === 0) {
      return this.items().size();
    }
    var count = 0;
    var list = resolved.list();
    for (var index = 0; index < list.length; index++) {
      if (list[index].isAnswerable()) {
        count = count + 1;
      }
    }
    return count;
  }
  
  withItems(items) {
    this.put(CheckList.Fields.CheckListItemMap, items);
    return this;
  }
  
  company() {
    var data = this.get(CheckList.Fields.COMPANY);
    var company = new Company(this.domain, data);
    return company;
  }
  
  map() {
    var map = new CheckListMap(this.domain);
    map.add(this);
    return map;
  }
 
  populate() {
    var items = this.items();
    items.populate()
    this.withItems(items);
    return this;
  }
  
  pointer() {
    var data = {'@rid': this.id()};
    var checklist = new CheckList(this.domain, data);
    return checklist;
  }
  
  copy() {
    var data = this._copy();
    return new CheckList(this.domain, data);
  }

  withCompany(company) {
    this.put(CheckList.Fields.COMPANY, company.pointer());
    return this;
  }

  createdBy() {
    return new User(this.domain, this.get(RcDomainObject.DefaultFields.CREATED_BY));
  }

  modifiedBy() {
    return new User(this.domain, this.get(RcDomainObject.DefaultFields.MODIFIED_BY));
  }
}